import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, MessagePlugin, Select, Steps } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import { connect } from 'react-redux';
import styled from 'styled-components';
import Form from '@/components/Form';
import FormItem from '@/components/Form/FormItem';
import Text from '@/components/Text';
import { bindDisasterRecoveryCluster } from '@/services/serviceDiscoveryService';
import { toTsOptions } from '@/utils/common';
import { resetBindRecovery, setBindRecoveryStep } from '@/slices/clusterSlice';
import TopicCheck from './TopicCheck';
import TopicHandle from './TopicHandle';
import ConfirmBind from './ConfirmBind';

const { StepItem } = Steps;

function BindModal({
  visible,
  handleClose,
  primaryClusterName,
  backupServers,
  bindRecovery,
  setBindRecoveryStep,
  resetBindRecovery,
}) {
  const [form] = Form.useForm();
  const handleRef = React.useRef();
  const [submitting, setSubmitting] = useState(false);
  const { currentStep, checking, canRetry, handleDone, errorTopics } = bindRecovery;
  const [params, setParams] = useState({}); // 缓存表单数据

  const doCheck = useMemoizedFn((values) => {
    setParams(values);
    setBindRecoveryStep(2);
  });

  const handleSubmit = useMemoizedFn(async () => {
    try {
      setSubmitting(true);

      await bindDisasterRecoveryCluster({
        ...params,
        currentProvideServiceCluster: params.primaryClusterName,
      });

      MessagePlugin.success('操作成功');
      handleClose(true);
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  });

  // 底部按钮
  const footer = useMemo(() => {
    switch (currentStep) {
      case 1:
        return (
          <Footer>
            <Button variant='outline' onClick={() => handleClose()}>
              取消
            </Button>
            <Button theme='primary' onClick={form.submit}>
              下一步
            </Button>
          </Footer>
        );
      case 2:
        return (
          <Footer>
            <Button variant='outline' onClick={() => handleClose()}>
              取消
            </Button>
            <Button
              theme='primary'
              loading={checking}
              disabled={checking}
              onClick={() => setBindRecoveryStep(errorTopics.length > 0 ? 3 : 4)} // 如果有错误的话就跳转下一步处理，否则直接进入绑定
            >
              下一步
            </Button>
          </Footer>
        );
      case 3:
        return (
          <Footer>
            {canRetry && (
              <Button theme='primary' onClick={() => handleRef.current.doRetry()}>
                重试并继续
              </Button>
            )}
            <Button variant='outline' onClick={() => handleClose()}>
              取消
            </Button>
            <Button
              theme='primary'
              disabled={!handleDone}
              loading={!handleDone}
              onClick={() => setBindRecoveryStep(4)}
            >
              下一步
            </Button>
          </Footer>
        );
      case 4:
        return (
          <Footer>
            <Button variant='outline' onClick={() => handleClose()}>
              取消
            </Button>
            <Button theme='primary' loading={submitting} onClick={handleSubmit}>
              完成绑定
            </Button>
          </Footer>
        );
      default:
        return null;
    }
  }, [
    currentStep,
    handleClose,
    submitting,
    form,
    setBindRecoveryStep,
    handleSubmit,
    checking,
    canRetry,
    handleDone,
    errorTopics,
  ]);

  const doInit = useMemoizedFn(async () => {
    form.setFieldsValue({ primaryClusterName });
  });

  useEffect(() => {
    if (visible) {
      doInit();
    } else {
      form.resetFields();
      resetBindRecovery();
    }
  }, [visible, form, doInit, resetBindRecovery]);

  return (
    <Dialog
      onConfirm={handleSubmit}
      visible={visible}
      footer={footer}
      header='添加备集群'
      width={currentStep > 1 ? 1000 : 550}
      destroyOnClose
      closeOnOverlayClick={false}
      placement='center'
      onClose={() => handleClose()}
    >
      <Root>
        <Steps
          current={currentStep}
          layout='horizontal'
          separator='line'
          sequence='positive'
          theme='default'
        >
          <StepItem value={1} title='选择备集群' />
          <StepItem value={2} title='检查Topic' />
          <StepItem value={3} title='处理Topic' />
          <StepItem value={4} title='完成绑定' />
        </Steps>
        {currentStep === 1 && (
          <Form form={form} onFinish={doCheck} style={{ width: '80%' }}>
            <FormItem label='集群名称' name='primaryClusterName'>
              <Text />
            </FormItem>
            <FormItem
              rules={[{ required: true, message: '请选择备集群' }]}
              label='备集群'
              name='recoveryClusterName'
            >
              <Select options={toTsOptions(backupServers)} clearable filterable />
            </FormItem>
          </Form>
        )}
        {currentStep === 2 && <TopicCheck {...params} />}
        {currentStep === 3 && <TopicHandle {...params} ref={handleRef} />}
        {currentStep === 4 && <ConfirmBind {...params} />}
      </Root>
    </Dialog>
  );
}

export default connect(
  (state) => ({
    backupServers: state.cluster.allList
      ?.filter((item) => item.isDisasterRecoveryCluster)
      .map((item) => ({ label: item.clusterName, value: item.clusterName })),
    bindRecovery: state.cluster.bindRecovery,
  }),
  {
    setBindRecoveryStep,
    resetBindRecovery,
  },
)(BindModal);

const Footer = styled.div`
  display: flex;
  gap: 6px;
  justify-content: flex-end;
  align-items: center;
`;

const Root = styled.div`
  .t-steps {
    padding: 0 10px 20px;
    .t-steps-item__title {
      font: var(--td-font-body-medium);
    }
    .t-steps-item__icon--number {
      width: 20px;
      height: 20px;
    }
  }
`;
